import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Inbox } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Pagination } from '@/components/ui/Pagination'
import { LawyerSelector } from '@/components/publications/inbox/LawyerSelector'
import { InboxStateTabs } from '@/components/publications/inbox/InboxStateTabs'
import { InboxFilterBar } from '@/components/publications/inbox/InboxFilterBar'
import { InboxList } from '@/components/publications/inbox/InboxList'
import { InboxSidePanel } from '@/components/publications/inbox/InboxSidePanel'
import { InboxBulkBar } from '@/components/publications/inbox/InboxBulkBar'
import { useInboxConference } from '@/components/publications/inbox/useInboxConference'
import { PublicationFullSheet } from '@/components/publications/inbox/PublicationFullSheet'
import { useAuth } from '@/contexts/AuthContext'
import { conferenceActionFor, filtersFromSearch, filtersToSearch, InboxFilters, updateFilters } from '@/lib/publication-inbox'
import { InboxItem, InboxPage, publicationInboxService } from '@/services/publication-inbox-service'
import { apiErrorMessage, teamService, TeamMember } from '@/services/team-service'

/** Publicações › Caixa de entrada: advogado e filtros ficam na URL. */
export function PublicationsInboxPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => filtersFromSearch(searchParams), [searchParams])
  const [members, setMembers] = useState<TeamMember[]>([])
  const [page, setPage] = useState<InboxPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<InboxItem | null>(null)
  // Ficha completa (gaveta larga): guarda o estado da caixa para as ações de conferência.
  const [full, setFull] = useState<Pick<InboxItem, 'id' | 'estado'> | null>(null)
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set())

  const change = (patch: Partial<InboxFilters>) => setSearchParams(filtersToSearch(updateFilters(filters, patch)))

  useEffect(() => {
    teamService.listMembers().then(setMembers).catch(() => setMembers([]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setPage(await publicationInboxService.list(filters))
      setChecked(new Set())
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível carregar as publicações'))
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  const conference = useInboxConference(load)
  const bulkAction = conferenceActionFor(filters.estado)
  const items = page?.items ?? []

  const toggle = (id: number) => setChecked((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const toggleAll = () => setChecked((current) => (current.size === items.length ? new Set() : new Set(items.map((i) => i.id))))

  const conferOne = async (item: InboxItem, action: 'conferir' | 'desconferir') => {
    if (await conference.run(action, [item.id])) setSelected(null)
  }

  const openFull = (item: InboxItem) => {
    setSelected(null)
    setFull({ id: item.id, estado: item.estado })
  }
  const closeFull = useCallback(() => setFull(null), [])

  const conferFull = async (action: 'conferir' | 'desconferir') => {
    if (full && (await conference.run(action, [full.id]))) setFull(null)
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Inbox className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Publicações</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LawyerSelector value={filters.advogado} members={members} currentUserId={user?.id} onChange={(advogado) => change({ advogado })} />
          <Button variant="outline" asChild><Link to="/publications/importacoes">Importações</Link></Button>
          <Button variant="outline" asChild><Link to="/publications/monitorados">Monitorados</Link></Button>
          <Button variant="ghost" asChild><Link to="/publications/painel">Painel</Link></Button>
        </div>
      </div>

      <InboxStateTabs value={filters.estado} counts={page?.counts} onChange={(estado) => change({ estado })} />
      <InboxFilterBar filters={filters} members={members} onChange={change} />

      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardContent className="flex flex-col gap-3">
          {bulkAction && !loading && (
            <InboxBulkBar
              action={bulkAction}
              total={items.length}
              selected={checked.size}
              busy={conference.busy}
              onToggleAll={toggleAll}
              onRun={() => conference.run(bulkAction, [...checked])}
            />
          )}
          <InboxList
            items={items}
            loading={loading}
            selectedId={selected?.id}
            onSelect={setSelected}
            checked={bulkAction ? checked : undefined}
            onToggle={bulkAction ? toggle : undefined}
          />
          {page && page.totalPages > 1 && (
            <Pagination
              currentPage={page.page}
              totalPages={page.totalPages}
              totalItems={page.total}
              onPageChange={(next) => change({ page: next })}
            />
          )}
        </CardContent>
      </Card>

      <InboxSidePanel
        item={selected}
        onClose={() => setSelected(null)}
        onOpenFull={openFull}
        onConference={conferOne}
        conferenceBusy={conference.busy}
      />
      <PublicationFullSheet
        publicationId={full?.id ?? null}
        estado={full?.estado}
        onClose={closeFull}
        onChanged={load}
        onConference={conferFull}
        conferenceBusy={conference.busy}
      />
    </div>
  )
}
